import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFitment, usePrevious } from '../../hooks/hooks';

import { isArrayWithLength } from '../../utils/validators/validators';

import { BundleType } from '../../types/graphql-types';
import { PRODUCT_TYPES } from '../../constants/products';

interface UseProductQuantityConfig {
  bundleType?: BundleType;
  initialQuantity?: number;
  initialRearQuantity?: number;
  initialSetQuantity?: Array<number>;
  productType?:
    | typeof PRODUCT_TYPES.WHEELS
    | typeof PRODUCT_TYPES.TIRES
    | typeof PRODUCT_TYPES.ACCESSORIES;
}

function useProductQuantity(config?: UseProductQuantityConfig) {
  const {
    initialQuantity,
    initialRearQuantity,
    initialSetQuantity,
    productType,
    bundleType
  } = config || {};

  const prevInitialSetQuantity = usePrevious(initialSetQuantity);
  const hasInitializedRef = useRef(false);
  const { selectedFitment } = useFitment();
  const isAccessoryOrPrebuiltPackage =
    productType === PRODUCT_TYPES.ACCESSORIES ||
    bundleType === BundleType.TireWheelPackage;
  const isStaggered =
    bundleType === BundleType.StaggeredSet || selectedFitment?.isStaggered;
  let defaultQuantity = 4;

  if (isAccessoryOrPrebuiltPackage) {
    defaultQuantity = 1;
  } else if (
    isStaggered ||
    (selectedFitment?.isDually && productType === PRODUCT_TYPES.WHEELS)
  ) {
    defaultQuantity = 2;
  } else if (selectedFitment?.isDually) {
    defaultQuantity = 6;
  }

  const [quantity, setQuantity] = useState(initialQuantity ?? defaultQuantity);
  const [rearQuantity, setRearQuantity] = useState(
    initialRearQuantity ?? defaultQuantity
  );
  const [productSetQuantity, setProductSetQuantity] = useState(
    Array.isArray(initialSetQuantity) ? initialSetQuantity : [defaultQuantity]
  );

  const customSetProductSetQuantity = useCallback(
    (quantity, index) => {
      const copyProductSetQuantity = JSON.parse(
        JSON.stringify(productSetQuantity)
      );
      copyProductSetQuantity[index] = quantity;

      setProductSetQuantity(copyProductSetQuantity);
    },
    [productSetQuantity]
  );

  const totalQuantity = useMemo(() => {
    if (bundleType === BundleType.StaggeredSet) {
      return (quantity || 0) + (rearQuantity || 0);
    } else if (isArrayWithLength(initialSetQuantity)) {
      return productSetQuantity!.reduce((sum, quantity) => sum + quantity, 0);
    } else {
      return quantity || 0;
    }
  }, [
    bundleType,
    initialSetQuantity,
    productSetQuantity,
    quantity,
    rearQuantity
  ]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      setQuantity(initialQuantity ?? defaultQuantity);
      setRearQuantity(initialRearQuantity ?? defaultQuantity);
    } else {
      hasInitializedRef.current = true;
    }
  }, [
    bundleType,
    productType,
    initialQuantity,
    initialRearQuantity,
    defaultQuantity
  ]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      if (
        initialSetQuantity &&
        JSON.stringify(prevInitialSetQuantity) !==
          JSON.stringify(initialSetQuantity)
      ) {
        setProductSetQuantity(initialSetQuantity);
      }
    } else {
      hasInitializedRef.current = true;
    }
  }, [initialSetQuantity, prevInitialSetQuantity]);

  return {
    defaultQuantity,
    productSetQuantity,
    quantity,
    rearQuantity,
    setProductSetQuantity: customSetProductSetQuantity,
    setQuantity,
    setRearQuantity,
    totalQuantity
  };
}

export default useProductQuantity;
